/**
 * CRM backend REST paths — aligned with OpenAPI on the call-center backend.
 */
export const API_ROUTES = {
    users: '/api/users',
    usersFromUms: '/api/users/from-ums',
    usersProfile: '/api/users/profile',
    unassignPhoneUser: (localUserId) => `/api/users/${encodeURIComponent(String(localUserId))}/unassign-phone-user`,
    forceLogout: (username) => `/api/users/${encodeURIComponent(username)}/force-logout`,
    provinces: '/api/provinces',
    districts: '/api/districts',
    villages: '/api/villages',
    branches: '/api/branches',
    countries: '/api/countries',
    sequences: '/api/sequences',
    leads: '/api/leads',
    leadAssignments: '/api/lead-assignments',
    leadUploadBatches: '/api/lead-upload-batches',
    leadUploadErrors: '/api/lead-upload-errors',
    blockLists: '/api/block-lists',
    callsAttempts: '/api/calls/attempts',
    callsSatisfactions: '/api/calls/satisfactions',
    cdrs: '/api/cdrs',
    queueLogsLatest: '/api/queue-logs/latest',
    userActivations: '/api/users',
    crmDepartments: '/api/crm/departments',
    coreBankingAccounts: '/api/crm/core-banking/accounts',
    /** NaqdPay partner lookup — `GET ?mobile=&page=&size=` */
    partners: '/api/crm/partners',
    partnerRevealTazkira: (partnerId) => `/api/crm/partners/${partnerId}/reveal-tazkira`,
    issues: '/api/issues',
    phones: '/api/phones',
    kases: '/api/kases',
    kasesDashboard: '/api/kases/dashboard',
    kaseReopenAssignments: (caseId) => `/api/kases/${caseId}/reopen-assignments`,
    kaseStatusHistory: (caseId) => `/api/kases/${caseId}/status-history`,
    caseAssignments: '/api/case-assignments',
    caseTransfers: '/api/case-transfers',
    caseTransferWorkflow: {
        request: (caseId) => `/api/case-transfers/workflow/cases/${caseId}/request`,
        approve: (id) => `/api/case-transfers/workflow/${id}/approve`,
        reject: (id) => `/api/case-transfers/workflow/${id}/reject`,
        cancel: (id) => `/api/case-transfers/workflow/${id}/cancel`,
        cancelByAssignment: '/api/case-transfers/workflow/cancel-by-assignment',
    },
    caseUpdates: '/api/case-updates',
    campaigns: '/api/campaigns',
    campaignsDashboard: '/api/campaigns/dashboard',
    campaignCustomers: (campaignId) => `/api/campaigns/${campaignId}/customers`,
    campaignAssign: (campaignId) => `/api/campaigns/${campaignId}/assign`,
    breaks: '/api/breaks',
    breaksActive: '/api/breaks/active',
    breaksEnd: '/api/breaks/end',
    breaksEndForUser: '/api/breaks/end-for-user',
    phonePause: '/api/phone-pause',
    phonePauseStatus: '/api/phone-pause/status',
    phonePauseClear: '/api/phone-pause/clear',
    /** Member-scoped live chat (inbox, history, send, read, DM) — STOMP topics are separate. */
    chat: {
        rooms: '/api/chat-rooms',
        mine: '/api/chat-rooms/mine',
        room: (roomId) => `/api/chat-rooms/${roomId}`,
        roomMessages: (roomId) => `/api/chat-rooms/${roomId}/messages`,
        roomMessage: (roomId, messageId) => `/api/chat-rooms/${roomId}/messages/${messageId}`,
        roomRead: (roomId) => `/api/chat-rooms/${roomId}/read`,
        roomMembers: (roomId) => `/api/chat-rooms/${roomId}/members`,
        roomMember: (roomId, userId) => `/api/chat-rooms/${roomId}/members/${encodeURIComponent(userId)}`,
        dm: (otherUserId) => `/api/chat-rooms/dm/${encodeURIComponent(otherUserId)}`,
        join: (roomId) => `/api/chat-rooms/${roomId}/join`,
    },
    /** Admin CRUD for rooms/members (permissions). */
    chatRooms: '/api/chat-rooms',
    files: '/api/files',
    fileUpload: '/api/files/upload',
    fileDownload: (id) => `/api/files/${id}/download`,
    /** Agent call greeting metadata — `GET ?category=grading&uploadedBy={email}` */
    fileMetadataByCategory: '/api/files/by',
    /** Agent call greeting — `GET ?category=grading&uploadedBy={email}` */
    fileDownloadByCategory: '/api/files/by/download',
    /**
     * NaqdPay merchant flight bookings (search / details / quote / reconcile).
     * `{requestId}` is the booking request id, not a PNR, ticket number, or passenger id.
     * Cancel and reschedule commits go through {@link callCenterFlightBookings} OTP, not
     * the merchant cancel/reschedule POST.
     */
    merchantFlightBookings: {
        one: (requestId) => `/api/merchant/flight-bookings/${encodeURIComponent(requestId)}`,
        cancelQuote: (requestId) => `/api/merchant/flight-bookings/${encodeURIComponent(requestId)}/cancel/quote`,
        cancelReconcile: (requestId) => `/api/merchant/flight-bookings/${encodeURIComponent(requestId)}/cancel/reconcile`,
        rescheduleSearch: (requestId) => `/api/merchant/flight-bookings/${encodeURIComponent(requestId)}/reschedule/search`,
        rescheduleDetails: (requestId) => `/api/merchant/flight-bookings/${encodeURIComponent(requestId)}/reschedule/details`,
        rescheduleReconcile: (requestId) => `/api/merchant/flight-bookings/${encodeURIComponent(requestId)}/reschedule/reconcile`,
    },
    /**
     * NaqdPay partner lookup for call-center agents (OpenAPI `searchPartners`).
     * Query is `mobile` (required), not `mobile.equals`.
     * Airplane Tickets sends this to MERCHANT_API_ORIGIN.
     */
    crmPartners: '/api/crm/partners',
    /**
     * Call-center ticket-booking list from the OTP Postman collection:
     * `GET /api/ticket-booking/requests?payerPartnerId.equals={payerId}`.
     */
    ticketBookingRequests: '/api/ticket-booking/requests',
    /**
     * Call-center payer OTP for cancel / reschedule. Confirm submits operationRequestId
     * and the OTP; referenceId from the OTP response is internal only.
     */
    callCenterFlightBookings: {
        cancellationOtp: (requestId) => `/api/call-center/flight-bookings/${encodeURIComponent(requestId)}/cancellation/otp`,
        cancellationConfirm: (requestId) => `/api/call-center/flight-bookings/${encodeURIComponent(requestId)}/cancellation/confirm`,
        rescheduleOtp: (requestId) => `/api/call-center/flight-bookings/${encodeURIComponent(requestId)}/reschedule/otp`,
        rescheduleConfirm: (requestId) => `/api/call-center/flight-bookings/${encodeURIComponent(requestId)}/reschedule/confirm`,
    },
    reportsExport: (reportName) => `/api/reports/${encodeURIComponent(reportName)}/export`,
    reports: {
        kpiDashboard: '/api/reports/kpi/dashboard',
        kpiAgents: '/api/reports/kpi/agents',
    },
    reportsV2: {
        incomingCallsExport: '/api/v2/reports/incoming-calls/export',
        outgoingCallsExport: '/api/v2/reports/outgoing-calls/export',
    },
    qstat: {
        dashboard: '/api/qstat/dashboard',
        voicemails: '/api/qstat/voicemails',
        voicemailDownload: (id) => `/api/qstat/voicemails/${id}/download`,
        incomingCalls: '/api/qstat/incoming-calls',
        outgoingCalls: '/api/qstat/outgoing-calls',
        queueReports: '/api/qstat/queue-reports',
        queueReportsV2: {
            answeredByAgent: '/api/qstat/v2/queue-reports/answered-calls/by-agent',
            answeredByQueue: '/api/qstat/v2/queue-reports/answered-calls/by-queue',
            disconnectionCause: '/api/qstat/v2/queue-reports/unanswered-calls/disconnection-cause',
            unansweredByQueue: '/api/qstat/v2/queue-reports/unanswered-calls/by-queue',
            distPerDay: '/api/qstat/v2/queue-reports/call-distribution/per-day',
            distPerHour: '/api/qstat/v2/queue-reports/call-distribution/per-hour',
            distPerDayOfWeek: '/api/qstat/v2/queue-reports/call-distribution/per-day-of-week',
            satisfactionByAgent: '/api/qstat/v2/queue-reports/call-satisfaction/customer-satisfaction-rate-by-agent',
            satisfactionDistribution: '/api/qstat/v2/queue-reports/call-satisfaction/satisfaction-distribution',
            detailedCallRecords: '/api/qstat/v2/queue-reports/call-satisfaction/detailed-call-records',
            queuePerformance: '/api/qstat/v2/queue-reports/queue-performance',
        },
        agentProductivity: '/api/qstat/agent-productivity',
        /** Same query params as `GET /api/qstat/agent-productivity` (not yet in deployed OpenAPI; CRM `SessionReportRestController`). */
        sessionReport: '/api/qstat/session-reports',
        agentStatistics: (username) => `/api/qstat/agents/${encodeURIComponent(username)}/statistics`,
        realtime: '/api/qstat/realtime',
        phoneUsers: '/api/qstat/phone-users',
        wallboard: '/api/qstat/wallboard',
        blacklist: '/api/qstat/blacklist',
        blacklistEntry: (id) => `/api/qstat/blacklist/${id}`,
        callRecordingMetadata: (uniqueid) => `/api/qstat/call-recordings/${encodeURIComponent(uniqueid)}/metadata`,
        callRecordingStream: (uniqueid) => `/api/qstat/call-recordings/${encodeURIComponent(uniqueid)}/stream`,
        callRecordingDownload: (uniqueid) => `/api/qstat/call-recordings/${encodeURIComponent(uniqueid)}/download`,
        analyticsBackfill: '/api/qstat/v2/analytics/backfill',
        analyticsSyncStats: '/api/qstat/v2/analytics/sync-stats',
    },
    /** Legacy / IAM — not in CRM OpenAPI; kept for settings screens until backend exposes equivalents. */
    legacy: {
        roles: '/api/roles',
        permissions: '/api/permissions',
        /** CRM home analytics — compose from `/api/kases` or future report API. */
        crmDashboard: '/api/dashboard',
    },
};
